<template>
	<div class="product-page-wrapper">
		<div class="product-wrapper" :style="{ height: height + 'px' }">
			<div class="product" ref="product">
				<product
					v-if="product"
					:product="product"
					:scrollPos="scrollPos"
					:docHeight="docHeight"
					:selectedBundleProducts.sync="selectedBundleProducts"
				/>
			</div>
		</div>
		<modal v-model="showMailInput" container-style="width: 500px; max-width:100%;">
			<div style="align-content: center; text-align: center">
				<h2>Meddela mig</h2>
				<p>Vi meddelar dig så fort produkten finns tillgänglig för beställning!</p>
				<input-text
					autofocus
					placeholder="E-postadress"
					type="email"
					inputmode="email"
					icon="mail"
					v-model="email"
					name="email"
					style="width: 301px; max-width:100%; margin: auto"
				/>
				<br>
				<text-button
					text="Meddela mig"
					center
					@click="saveEmail()"
				/>
			</div>
		</modal>
		<!-- Semi-sticky add to cart button -->
		<div v-if="showStickyBuyButton" class="sticky-add-to-cart" :class="showAddToCart ? 'show-sticky-button' : 'hide-sticky-button'">
			<product-price :product="product" :row="product.price.price === product.regularPrice.price"/>
			<buy-button
				buttonSize="large"
				:product="product"
				:selectedBundleProducts="selectedBundleProducts"
				@triggerInsurance="triggerInsurance"
			/>
		</div>
		<!-- End semi-sticky add to cart button -->
	</div>
</template>

<script>
import waja from 'waja';
import Vue from 'vue';
import alert from 'alert';
import bus from 'eventbus';
import router from 'router';
import mq, { isDesktop } from 'mediaQuery';
import store from 'datastore';
import tagHandler from 'tagHandler';
import metaHandler from 'metaHandler';
import { prerender } from 'staticData';
import { mapGetters, mapState } from 'vuex';
import { error, convertToCdnUrl, isProductInSecretMode, onElementHeightChange, getDocumentHeight } from 'utils';
import { debounce } from 'lodash';
import { trackImpression } from 'gtm';
import { updateCategoryHighlights } from 'utils/highlight';
import { setSectionAndCategories } from 'datastore/navStatus';
import { triggerVideoly } from 'videoly';

let updateTimerId = null;

export default {
	data () {
		return {
			product: null,
			currentId: null,
			productPages: new Map(),
			canGoNext: false,
			canGoPrev: false,
			loadingNext: false,
			loadingPrev: false,
			navigationBySlide: false,
			tipsLoaded: false,
			productComponentLoaded: false,
			metaLoaded: false,
			activeIndex: 0,
			useSlideAnimation: true,
			height: 2000,
			addToCartPos: {
				top: 0,
				bottom: 0,
			},
			footerHeight: 0,
			scrollPos: 0,
			docHeight: 0,
			clickedBuy: false,
			showMailInput: false,
			email: null,
			zendeskClasses: null,
			selectedBundleProducts: [],
		};
	},
	handlesMeta: true,
	willSendScrollTriggerEvent: true,
	handleTakeovers: true,
	handleNavigation () {
		return true;
	},
	beforeRouteEnter (to, frm, next) {
		let page = parseInt(frm.query.page);
		if (isNaN(page)) {
			page = 1;
		}
		const id = parseInt(to.params.id);
		const data = {
			touchpoint: isDesktop() ? 'DESKTOP' : 'MOBILE'
		};

		const pass = to.query.pass;

		// Password protected prices
		if (pass) {
			data.pass = pass;
		}

		const req = waja.get('product/' + id).data(data);
		req.on('success', (res) => {
			next((vm) => {
				vm.disableAnimationForOneTick();
				vm.productComponentLoaded = false;
				vm.metaLoaded = false;
				vm.tipsLoaded = false;
				vm.product = res.data.product;
				vm.currentId = res.data.product.id;
				vm.page = page;
				if (! prerender) {
					store.commit('viewHistory/addProduct', res.data.product, pass);
				}
				vm.updateBreadcrumbsAndTitle(res.data.product);
				vm.getProductTips();
				bus.$emit('saved-scroll.apply');
			});
		}).on('404', res => {
			res.json().then(json => {
				let data = {'product': null};
				if (json instanceof Object) {
					data.product = json;
				}
				next(error(404, 'Produkten kan inte hittas', data));
			});
		})
		.on('error', () => {
			next(error(500, 'Det går för tillfället inte att komma åt denna sida. Försök gärna senare.'));
		}).go();
	},
	beforeRouteUpdate (to, frm, next) {
		if (~~to.params.id === ~~frm.params.id) {
			next(vm => {
				vm.onPageLoad();
			});
			return;
		}
		const id = ~~to.params.id;
		const data = {
			touchpoint: isDesktop() ? 'DESKTOP' : 'MOBILE'
		};

		const pass = to.query.pass;

		// Password protected prices
		if (pass) {
			data.pass = pass;
		}

		const req = waja.get('product/' + id).data(data);
		req.on('success', res => {
			next(vm => {
				vm.productPages = new Map();
				vm.product = res.data.product;
				vm.canGoNext = false;
				vm.canGoPrev = false;

				vm.currentId = res.data.product.id;
				if (! prerender) {
					store.commit('viewHistory/addProduct', res.data.product, pass);
				}

				vm.updateBreadcrumbsAndTitle(res.data.product);
				vm.metaLoaded = false;
				vm.tipsLoaded = false;
				vm.productComponentLoaded = false;

				vm.getProductTips();
				bus.$emit('saved-scroll.apply');
			});
		}).on('404', () => {
			next(error(404, 'Produkten kan inte hittas :('));
		}).go();
	},
	methods: {
		loadProductAgain() {
			let page = 1;
			const id = parseInt(this.$route.params.id);

			const data = {
				touchpoint: isDesktop() ? 'DESKTOP' : 'MOBILE'
			};

			// Password protected prices
			const pass = this.$route.params.pass;
			if (pass) {
				data.pass = pass;
			}

			const req = waja.get('product/' + id).data(data);
			req.on('success', (res) => {
				this.disableAnimationForOneTick();
				this.productComponentLoaded = false;
				this.metaLoaded = false;
				this.tipsLoaded = false;
				this.product = res.data.product;
				this.currentId = res.data.product.id;
				this.page = page;
				if (! prerender) {
					store.commit('viewHistory/addProduct', res.data.product, pass);
				}
				this.updateBreadcrumbsAndTitle(res.data.product);

				this.getProductTips();
				bus.$emit('saved-scroll.apply');
				bus.$emit('loadedProductAgain', res.data.product);
			}).on('404', res => {
				res.json().then(json => {
					let data = {'product': null};
					if (json instanceof Object) {
						data.product = json;
					}
					next(error(404, 'Produkten kan inte hittas', data));
				});
			}).go();
		},
		nowShowMailInput() {
			this.showMailInput = true;
		},
		saveEmail() {
			const search = this.email.search('@');
			if (! this.email || this.email.length < 3 || search === -1) {
				alert('E-postadressen är ogiltig!');
				return;
			}

			const data = {
				productId: this.currentId,
				email: this.email,
			};

			waja.post('product/send-mail-when-available')
				.data(data)
				.on('success', res => {
					this.showMailInput = false;
					this.email = null;
					alert('Du kommer få ett mail av oss när produkten blir tillgänglig!');
				})
				.on('405', res => {
					alert('Den här e-posten är redan anmäld för den här produkten!');
				})
				.on('error', res => {
					alert('Något gick fel!');
				}).go();
		},
		updateTakeover () {
			// AB-test (Dölj takeover på produktsidor)
			if (window.useProductTakeovers === false) {
				bus.$emit('takeover:update', null);
				return;
			}

			// Uppdatera highlights
			if (this.currentCategoryId && mq.current >= mq.lg) { // Mobilanvändare behöver ej hämta takeovers
				updateCategoryHighlights(this.currentCategoryId);
			}
		},
		/**
		 * Callback that is called when product tips have loaded.
		 */
		onTipsLoad () {
			this.tipsLoaded = true;
			this.onPageLoad();
		},
		onMetaLoad () {
			this.metaLoaded = true;
			this.onPageLoad();
		},
		onProductComponentLoad (product) {
			// Verify that the product component which has loaded is the current product.
			if (product.id === this.product.id) {
				this.productComponentLoaded = true;
				// Update page height so all content is visible.
				this.updateHeight();
				Vue.nextTick(() => {
					this.getDivElements();
					this.onPageLoad();
				});
			}
		},
		onPageLoad () {
			if (this.tipsLoaded && this.productComponentLoaded && this.metaLoaded) {
				// @TODO Router handles prerender communication temporarily for
				// the product page, until the product page loading has been fixed. It
				// seems like this method gets called multiple times per page load
				// resulting in weird prerendering behavior.
				// store.dispatch('prerender/pageLoaded');
			}
		},
		// Gets position of add to cart div after page loaded to control when to show/hide sticky add to cart
		getDivElements () {
			let loadedElements = document.getElementById('footer') !== null &&
				document.getElementById('add-product-to-cart') !== null &&
				document.getElementById('launcher') !== null;
			if (loadedElements) {
				this.footerHeight = document.getElementById('footer').getBoundingClientRect().height;
				let properties = document.getElementById('add-product-to-cart').getBoundingClientRect();
				this.zendeskClasses = document.getElementById('launcher').classList;
				this.zendeskClasses.add('hide-zendesk-margin');

				this.addToCartPos = {
					top: properties.top + this.scrollPos,
					bottom: properties.bottom + this.scrollPos,
				};
			}
		},
		updateBreadcrumbsAndTitle (product) {
			// Använd senaste route för att lista ut ifall vi kommer från en
			// kategori som inte är mainCategoryPath, men som fortfarande är valid
			// för produkten i fråga.

			function findPath (categories, targetId) {
				for (const cat of categories) {
					if (cat.id === targetId) {
						return [cat];
					}
					const ret = findPath(cat.subCategories, targetId);
					if (ret) {
						ret.unshift(cat);
						return ret;
					}
				}
				return false;
			}

			if (! product) {
				return;
			}

			let currentSection = null;
			const navStatus = this.navStatus;

			if (navStatus.menu.staticTree) {
				const targetSectionId = navStatus.section ? navStatus.section.id : null;

				if (targetSectionId) {
					for (const section of navStatus.menu.staticTree) { // hitta nuvarande sektion
						if (section.id !== targetSectionId) {
							continue;
						}

						currentSection = section;
						break;
					}
				}
			}

			let bestCandidate = null;
			if (currentSection) { // Har vi en sektion i navstatus?
				for (const category of product.categories) {
					const path = findPath(currentSection.categories, category.id);

					if (! path) {
						continue;
					}

					let score = 0;
					// Räkna ut hur många nivåer av denna path som matchar med path på routen innan
					for (const i in navStatus.categories) {
						if (i >= path.length || navStatus.categories[i].id !== path[i].id) {
							break;
						}
						++score;
					}

					if (bestCandidate === null || bestCandidate.score < score) {
						bestCandidate = {
							id: category.id,
							score,
							path,
						};
					}
				}
			}

			if (bestCandidate) {
				setSectionAndCategories(currentSection, bestCandidate.path);
			} else {
				const categoryPath = product.mainCategoryPath || [ product.categories[0]];
				setSectionAndCategories(product.section, categoryPath);
			}

			router.updateParams({
				id: product.id,
				name: product.name,
			});
		},
		disableAnimationForOneTick () {
			this.useSlideAnimation = false;
			setTimeout(() => {
				this.useSlideAnimation = true;
			}, 100);
		},
		updateHeight () {
			if ( ! this.$refs.product) {
				return;
			}
			const product = this.$refs.product;
			if (product) {
				this.height = this.$refs.product.getBoundingClientRect().height;
			}
		},
		getProductTips () {
			this.$nextTick(() => {
				this.tipsLoaded = false;
				store.commit('productTips/setShowTopLists', false);
				store.commit('productTips/setShowLists', true);
				if (! this.product) {
					console.error('invalid stuff', this.product);
				}
				store.dispatch('productTips/fetchTips', this.product);
			});
		},
		handleScroll (e) {
			this.scrollPos = window.scrollY;
		},
		deleteOGTags () {
			metaHandler.delete('og:image');
			metaHandler.delete('og:url');
			metaHandler.delete('og:type');
			metaHandler.delete('og:title');
			metaHandler.delete('og:description');
			metaHandler.delete('fb.app_id');
		},
		updateMeta () {
			const product = this.product;
			// Set meta
			let description = 'Köp ' + product.name + ' med fri frakt för medlemmar ' +
				'✓ Låga priser  ✓ Öppet köp 30 dagar ✓ Snabba leveranser - Köp online ' +
				'eller i butik!';

			if (product.metaDescription) {
				description = product.metaDescription;
			}
			metaHandler.set('description', description);
			metaHandler.set('og:image', convertToCdnUrl(product.images[0].large));
			metaHandler.set('og:url', location.href);
			metaHandler.set('og:type', 'article');
			metaHandler.set('og:title', product.name);
			metaHandler.set('og:description', description);
			metaHandler.set('fb.app_id', '1670825789818570');

			// If it is discontinued and not in stock, then it should have robots = noindex, follow.
			// Same goes for fyndwares.
			if (product.isFyndware) {
				metaHandler.set('robots', 'noindex, follow');
			}

			// Set the canonical tag of the product. If the product has no
			// variants, it will point to itself.
			// const canonical = product.variants ? product.variants.canonicalVariant : product;

			// 2019-04-25: We will be letting all products point to themselves
			// from now on, in hopes that all product variants will be indexed,
			// so that customers can search for more specific product details
			// (such as color, size) and get the correct products in their search results.
			const canonical = product;

			let canonicalId = canonical.id;
			let canonicalName = canonical.name

			if (this.product.canonicalLink) {
				canonicalId = this.product.canonicalLink.match(/\d{1,}/s)[0];
				canonicalName = this.product.canonicalLink.split(/-(.*)/s)[1];
			}

			tagHandler.set({
				id: 'canonical',
				type: 'link',
				attributes: {
					rel: 'canonical',
					href: router.routeToUrl(router.route({
							name: 'product',
							params: {
								id: canonicalId,
								name: canonicalName,
							},
					})),
				},
			});
			this.onMetaLoad();
		},
		buyButtonClick (product) {
			bus.$emit('product-page.add-to-cart', 'main-prod');
			this.clickedBuy = true;
		},
		triggerInsurance () {
			bus.$emit('sticky.triggerInsurance');
		}
	},
	computed: {
		showStickyBuyButton () {
			return this.product && this.cookieConsent;
		},
		currentCategoryId () {
			let categories = this.navStatus.categories;
			if (! categories.length) {
				categories = this.product.mainCategoryPath;
				if (! (categories && categories.length)) {
					return null;
				}
			}
			return categories[categories.length - 1].id;
		},
		...mapGetters({
			cookieConsent: 'cookieConsent/necessary',
			isLoggedIn: 'user/isLoggedIn',
		}),
		...mapState({
			navStatus: state => state.navStatus,
			user: state => state.user,
		}),
		showAddToCart () {
			let bottomScroll = this.scrollPos + window.innerHeight;
			let footerTop = this.docHeight-this.footerHeight;
			let showElement = bottomScroll < this.addToCartPos.top || (this.scrollPos > this.addToCartPos.bottom && bottomScroll < footerTop);

			if (this.zendeskClasses) {
				if (showElement && this.zendeskClasses.contains('hide-zendesk-margin')) {
					this.zendeskClasses.add('show-zendesk-margin');
					this.zendeskClasses.remove('hide-zendesk-margin');
				} else if (! showElement && this.zendeskClasses.contains('show-zendesk-margin')) {
					this.zendeskClasses.add('hide-zendesk-margin');
					this.zendeskClasses.remove('show-zendesk-margin');
				}
			}

			return showElement;
		},
	},
	destroyed () {
		window.removeEventListener('scroll', this.handleScroll);
		window.removeEventListener('resize', this.getDivElements);
		clearInterval(updateTimerId);
		updateTimerId = null;
	},
	watch: {
		product (to, frm) {
			if (to && frm && to.id === frm.id) {
				return;
			}
			// Logga visningen
			waja.post('product/' + to.id + '/visit').go();

			this.updateTakeover();
			this.updateMeta();

			let alternativeCategory = this.navStatus && this.navStatus.section ? this.navStatus.section.name : '';
			// Format category names correctly
			const regex = /\/|\s+\/\s+/g;
			this.navStatus.categories.forEach(cat => {
				alternativeCategory += '/' + cat.name.replace(regex, ' ');
			});

			const data = {
				rating: true,
				stock: true,
				event: 'eec_detail',
				coupon: true,
				alternativeCategory: alternativeCategory,
			}

			trackImpression([to], 'Product Page', data);
		},
		docHeight (to) {
			this.getDivElements();
		},
	},
	mounted () {
		triggerVideoly();
		const vm = this;

		onElementHeightChange(document.body, function () {
			vm.docHeight = getDocumentHeight();
			bus.$emit('update:siteHeight');
		});

		bus.$on('product-component:loaded', this.onProductComponentLoad);
		bus.$on('productTips:rendered', this.onTipsLoad);
		bus.$on('show-mail-input-modal', this.nowShowMailInput);
		window.addEventListener('scroll', vm.handleScroll);
		window.addEventListener('resize', debounce(vm.getDivElements, 400));

		if (prerender) {
			// Height updates will be handled separately when prerendering.
			return;
		}
		// Ugly hack to make the slider update it's height.
		// We cannot put a watcher on the height of the content
		// For example when images gets loaded into the description or when a user presses the 'load more reviews'.
		// We could emit an event from the product when the 'load more' button is pressed and updating then, but that will not help us with the dynamic content in the descriptions.
		updateTimerId = setInterval(this.updateHeight.bind(this), 500);
		if (this.user) {
			this.email = this.user.email;
		}
		bus.$on('userLoggedInOrOut:loadProduct', this.loadProductAgain);
	},
	beforeDestroy () {
		this.deleteOGTags();
		bus.$off('product-component:loaded', this.onProductComponentLoad);
		bus.$off('productTips:rendered', this.onTipsLoad);
	},
};
</script>